import { useState } from "react";
import { toast } from "sonner";
import { ShoppingCart, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { getProduct } from "@/data/products";

/**
 * Reusable Buy Now button.
 *
 * Behaviour:
 *   1. If the product in the catalog has a `paymentLink` (Stripe Payment Link),
 *      redirect the user straight there. Stripe collects the email on its own
 *      hosted page, and its success_url should bring the buyer back to
 *      `/thank-you?session_id={CHECKOUT_SESSION_ID}&package=<slug>` so our
 *      branded thank-you + PDF downloads still work.
 *   2. Otherwise fall back to the legacy server-side Checkout Session flow.
 */
export default function BuyNowButton({
  className = "",
  testid = "buy-now",
  label = "Buy now, $29",
  packageId = "picture-day-system",
  variant = "primary",
}) {
  const [loading, setLoading] = useState(false);
  const product = getProduct(packageId);
  const paymentLink = product?.paymentLink;

  const handleClick = async () => {
    if (paymentLink) {
      setLoading(true);
      window.location.href = paymentLink;
      return;
    }

    // Legacy fallback, dynamic Checkout Session
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch {
      toast.error("Couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };

  const baseStyles =
    variant === "ghost"
      ? "inline-flex items-center gap-2 bg-white text-brand-sageDark hover:bg-brand-bg transition-colors px-7 py-3.5 rounded-full font-semibold disabled:opacity-70"
      : "inline-flex items-center gap-2 bg-brand-sage text-white hover:bg-brand-sageDark transition-all px-7 py-3.5 rounded-full font-medium shadow-soft hover:-translate-y-0.5 disabled:opacity-70";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      data-testid={testid}
      className={`${baseStyles} ${className}`}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
      {loading ? "Opening secure checkout…" : label}
    </button>
  );
}
